import Link from "next/link";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "gold" | "light";
type Size = "sm" | "md" | "lg";

const base =
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold " +
  "transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-out-quint " +
  "active:translate-y-px disabled:pointer-events-none disabled:opacity-50";

const variants: Record<Variant, string> = {
  primary: "bg-forest text-sand hover:bg-moss shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]",
  secondary: "border border-forest/80 text-forest hover:bg-forest hover:text-sand",
  ghost: "text-moss hover:bg-sand hover:text-forest",
  gold: "bg-gold text-ink hover:bg-[#d4c29d]",
  light: "border border-forest-border text-sand hover:border-gold hover:text-white",
};

const sizes: Record<Size, string> = {
  sm: "h-9 px-3.5 text-[13px] rounded-sm",
  md: "h-11 px-4.5 text-sm",
  lg: "h-12 px-5.5 text-[15px]",
};

export function buttonClass(variant: Variant = "primary", size: Size = "md", className?: string) {
  return cn(base, variants[variant], sizes[size], className);
}

type ButtonProps = React.ComponentProps<"button"> & { variant?: Variant; size?: Size };

export function Button({ variant, size, className, type = "button", ...props }: ButtonProps) {
  return <button type={type} className={buttonClass(variant, size, className)} {...props} />;
}

type ButtonLinkProps = React.ComponentProps<typeof Link> & { variant?: Variant; size?: Size };

export function ButtonLink({ variant, size, className, ...props }: ButtonLinkProps) {
  return <Link className={buttonClass(variant, size, className)} {...props} />;
}
